import { useState, memo } from "react";
import { useTranslation } from "react-i18next";
import {
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	IconButton,
	TextField,
	useColorScheme,
} from "@mui/material";
import JsonEditor from "@monaco-editor/react";
// cmps
import Svg from "../svg";
import Toast from "../Toast";
// hooks
import useSend from "../../hooks/useSend";
// services
import User from "../../services/user";
// types
import type { CmdCreateGroup } from "../../types/gupport";
import type { GroupName, CategoryName, GatewayData, FilteredGroups } from "../../types/firmware-groups";

const JSON_DATA = JSON.stringify({ apps: {} }, null, 2);

type Props = Readonly<{
	groupCategory: CategoryName;
	groups: FilteredGroups;
	fetchGroups: () => void;
}>;

const parseAddGroupJson = (groupJson: string | undefined) => {
	try {
		return groupJson ? JSON.parse(groupJson) as GatewayData : undefined;
	} catch {
		return undefined;
	}
};

const CreateGroup = ({ groupCategory, groups, fetchGroups }: Props) => {
	const send = useSend();
	const { t } = useTranslation();
	const { mode, systemMode } = useColorScheme();

	const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
	const [createGroupName, setCreateGroupName] = useState<GroupName | undefined>(undefined);
	const [groupContent, setGroupContent] = useState(JSON_DATA);
	const [inProgress, setInProgress] = useState(false);
	const [showCreateGroupSuccess, setShowCreateGroupSuccess] = useState<number | undefined>(undefined);
	const [showCreateGroupError, setShowCreateGroupError] = useState<number | undefined>(undefined);

	const allGroups = groups.flatMap((_group) => (_group.names));
	const parsedAddGroupJson = parseAddGroupJson(groupContent);

	const isAddingGroupInvalid = !parsedAddGroupJson?.apps ||
		typeof parsedAddGroupJson.apps !== "object" ||
		!createGroupName || allGroups.includes(createGroupName);

	const handleCancel = () => {
		setCreateGroupName(undefined);
		setShowCreateGroupDialog(false);
		setGroupContent(JSON_DATA);
	};

	const handleCreateGroup = () => {
		if (isAddingGroupInvalid) {
			return;
		}
		setInProgress(true);
		const cmd = {
			action: "createGroup",
			group: createGroupName,
			groupContent: groupCategory === "root" ? parsedAddGroupJson : { ...parsedAddGroupJson, category: groupCategory },
		} as const satisfies CmdCreateGroup;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setShowCreateGroupError(undefined);
				setShowCreateGroupSuccess(Date.now());
				fetchGroups();
			} else {
				setShowCreateGroupError(Date.now());
			}
			setInProgress(false);
			setShowCreateGroupDialog(false);
			setCreateGroupName(undefined);
			setGroupContent(JSON_DATA);
		});
	};

	return (
		<>
			{User.hasLevel("fw_group_manage") &&
				<IconButton
					className="btn-add-group-dialog-open"
					onClick={() => (setShowCreateGroupDialog(true))}
					aria-label={t("firmwareGroups.actions.createGroup.dialogTitle")}
				>
					<Svg src="navigation/add.svg" />
				</IconButton>
			}
			<Dialog
				fullWidth={true}
				open={showCreateGroupDialog}
				onClose={handleCancel}
			>
				<DialogTitle>{t("firmwareGroups.actions.createGroup.dialogTitle")}</DialogTitle>
				<DialogContent>
					<TextField
						placeholder={t("firmwareGroups.actions.createGroup.placeholder")}
						fullWidth={true}
						value={createGroupName}
						onChange={(event) => (setCreateGroupName(event.target.value as GroupName))}
					/>
					<JsonEditor
						height="175px"
						defaultLanguage="json"
						language="json"
						value={groupContent}
						theme={(systemMode ?? mode) === "dark" ? "vs-dark" : "light"}
						onChange={(value) => (setGroupContent(value ?? ""))}
						options={{
							minimap: { enabled: false },
							formatOnPaste: true,
							formatOnType: true,
							automaticLayout: true,
							fontSize: 15,
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancel}>{t("dialog.cancel")}</Button>
					<Button
						variant="contained"
						disabled={isAddingGroupInvalid}
						loading={inProgress}
						onClick={handleCreateGroup}
					>
						{t("dialog.add")}
					</Button>
				</DialogActions>
			</Dialog>
			<Toast
				autoHideDuration={3000}
				severity="success"
				open={showCreateGroupSuccess}
				onClose={setShowCreateGroupSuccess}
				message={t("firmwareGroups.actions.createGroup.successMsg")}
			/>
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showCreateGroupError}
				onClose={setShowCreateGroupError}
				message={t("toast.genericErrorMsg")}
			/>
		</>
	);
};

export default memo(CreateGroup);
